import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { gql } from 'graphql-tag';
import { Subscription, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';

import {
    CreateTransactionGQL,
    GetAegisDocumentLinkGQL,
    GetConfigValuesGQL,
    TransactionStage,
    TransactionType,
} from '../../../../../generated/graphql.generated';
import { quickJsActions } from '../../../../quickjs/quickjs.actions';
import { selectNavigatorCurrentStepId } from '../../../../quickjs/quickjs.feature';
import { JobFinancialsActions } from '../../../job-financials/job-financials.actions';
import { JobInvoiceActions } from '../../../job-invoice/job-invoice.actions';
import { selectJob } from '../../../job-tool.reducer';

import { NavBaseComponent } from './nav-base';

/**
 * End-of-move billing: create the invoice, record what the customer paid, send the invoice.
 *
 * Meeting item 4. Mounted on the `collect_payment` step, which existed with two `<P>` placeholders
 * and a `helpText` describing behaviour that was never built.
 *
 * **This records a payment; it does not take one.** There is no card-payment-on-phone capability in
 * this system and this component does not add one — the crew has already been handed cash, a cheque
 * or a card receipt, and this is the ledger entry for it. That is why provider-backed payment
 * methods are filtered out below rather than offered and handled.
 *
 * ── The order is load-bearing ────────────────────────────────────────────────────────────────
 *
 * **create invoice → record payment → send.** Not interchangeable:
 *
 *  - **Send finalizes.** The app's own confirmation says it: no further charges can be added to the
 *    invoiced events afterwards. So send is last, behind its own confirm, and gone once done.
 *  - **The transaction needs the invoice to exist** so it can carry `invoiceId` and show against the
 *    invoice rather than floating on the job.
 *  - **Recording the payment first would lose that link**, and recording it after the send would
 *    post against a finalized invoice.
 *
 * A signing step must NOT be added before the send. Recording the payment calls `updateJobTotals`,
 * which emits `jobs.updateTotals`, which marks every invoice artifact on the job `outdated`; `send`
 * then regenerates the document, and `aegis.regenerateDocument` refuses to regenerate anything
 * carrying signatures. Sign-then-send therefore fails at the last step, after the customer has
 * signed. If customer signing is ever wanted it goes *after* the send — see the marked slot at the
 * end of the template, and P13 in `docs/crew-navigator.md`.
 */

/**
 * Everything the step needs about the job's invoices, in one query.
 *
 * The job in the store already carries `invoices` — but only `...InvoiceWithArtifacts`, which has no
 * `events`, so it cannot answer "is *this* event already invoiced?". That question decides whether
 * the crew is offered a create button or shown an existing invoice, and getting it wrong means a
 * second invoice for the same event. Hence the explicit `events { id }`.
 */
const INVOICES_FOR_JOB = gql`
query NavInvoicesForJob($jobId: String!) {
    invoices(filter: { jobId: $jobId }, limit: 50) {
        invoices {
            id
            invoiceNumber
            sentAt
            voidedAt
            deletedAt
            total
            paidTotal
            events { id }
            artifacts { id name contentType attributes url signedUrl }
        }
    }
}
`;

interface NavInvoice {
    id: string;
    invoiceNumber?: number | null;
    sentAt?: number | null;
    voidedAt?: number | null;
    deletedAt?: number | null;
    total?: number | null;
    paidTotal?: number | null;
    events?: Array<{ id: string }> | null;
    artifacts?: Array<{
        id: string;
        attributes?: string[] | null;
        url?: string | null;
        signedUrl?: string | null;
    }> | null;
}

/** How long to wait on an effect's success/error before giving the crew their button back. */
const ACTION_TIMEOUT_MS = 30_000;

interface PaymentMethod {
    id: string;
    name: string;
}

@Component({
    selector: 'app-nav-collect-payment',
    standalone: true,
    imports: [ CommonModule, FormsModule ],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }

        .pay-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07); overflow: hidden;
        }
        .pay-header {
            padding: 0.9rem 1.1rem;
            font-size: 1rem; font-weight: 700; color: #1a1a1a;
            border-bottom: 1px solid #eee;
        }
        .pay-body { padding: 0.9rem 1.1rem 1.1rem; }

        /* Balance summary */
        .balance { background: #f7f9fb; border-radius: 8px; padding: 0.7rem 0.9rem; }
        .balance-row {
            display: flex; justify-content: space-between; align-items: baseline;
            font-size: 0.9rem; color: #555; padding: 0.15rem 0;
        }
        .balance-row b { color: #1a1a1a; font-variant-numeric: tabular-nums; }
        .balance-row.due {
            border-top: 1px solid #e2e8ee; margin-top: 0.4rem; padding-top: 0.5rem;
            font-size: 1rem; font-weight: 700; color: #1a1a1a;
        }
        .balance-row.due b { font-size: 1.15rem; }

        /* Numbered blocks */
        .block { margin-top: 1.1rem; }
        .block-title {
            display: flex; align-items: center; gap: 0.5rem;
            font-size: 0.78rem; font-weight: 700; text-transform: uppercase;
            letter-spacing: 0.05em; color: #666; margin-bottom: 0.55rem;
        }
        .block-num {
            display: flex; align-items: center; justify-content: center;
            width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0;
            background: #d7dee6; color: white; font-size: 0.72rem; font-weight: 800;
        }
        .block.active .block-num { background: #2d6a2d; }
        .block.done .block-num { background: #16a34a; }
        .block.locked { opacity: 0.45; pointer-events: none; }

        .done-line {
            display: flex; align-items: center; gap: 0.45rem;
            font-size: 0.92rem; color: #15803d; font-weight: 600;
        }
        .muted { font-size: 0.85rem; color: #777; }

        /* Buttons */
        .btn {
            display: flex; align-items: center; justify-content: center; gap: 0.5rem;
            width: 100%; min-height: 46px; padding: 0.75rem 1rem;
            border-radius: 8px; border: none; cursor: pointer;
            font-family: inherit; font-size: 0.95rem; font-weight: 700;
        }
        .btn:disabled { opacity: 0.55; cursor: default; }
        .btn-primary { background: #2d6a2d; color: white; }
        .btn-send { background: #b45309; color: white; }
        .btn-ghost {
            background: white; color: #2d6a2d; border: 1.5px solid #cbd5cb;
            font-weight: 600; min-height: 42px;
        }
        .btn + .btn, .btn + .btn-ghost { margin-top: 0.5rem; }

        /* Form */
        .field { margin-bottom: 0.7rem; }
        .field-label {
            display: block; font-size: 0.82rem; font-weight: 600;
            color: #555; margin-bottom: 0.3rem;
        }
        .amount-wrap { display: flex; align-items: center; gap: 0.5rem; }
        .amount-input {
            flex: 1; min-width: 0; padding: 0.65rem 0.75rem;
            border: 1.5px solid #ced4da; border-radius: 8px;
            font-family: inherit; font-size: 1.15rem; font-weight: 700;
            font-variant-numeric: tabular-nums;
        }
        .max-btn {
            flex-shrink: 0; padding: 0.55rem 0.8rem; border-radius: 8px;
            background: #eef4ee; color: #2d6a2d; border: none;
            font-family: inherit; font-size: 0.82rem; font-weight: 700; cursor: pointer;
        }
        .methods { display: flex; flex-wrap: wrap; gap: 0.45rem; }
        .method-chip {
            padding: 0.55rem 0.85rem; border-radius: 20px;
            border: 1.5px solid #ced4da; background: white;
            font-family: inherit; font-size: 0.88rem; cursor: pointer; color: #333;
        }
        .method-chip.selected {
            border-color: #2d6a2d; background: #eef4ee;
            color: #2d6a2d; font-weight: 700;
        }
        .note-input {
            width: 100%; padding: 0.6rem 0.75rem; border: 1.5px solid #ced4da;
            border-radius: 8px; font-family: inherit; font-size: 0.9rem;
        }

        /* Send warning */
        .warn {
            display: flex; gap: 0.55rem; align-items: flex-start;
            background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px;
            padding: 0.65rem 0.8rem; margin-bottom: 0.6rem;
            font-size: 0.85rem; color: #9a3412; line-height: 1.35;
        }
        .warn i { margin-top: 0.1rem; }

        .error-text { color: #dc2626; font-size: 0.85rem; margin-top: 0.5rem; }

        /* Full-screen iframe overlay — same shape as nav-pandadoc-signer's. */
        .iframe-overlay {
            position: fixed; inset: 0; z-index: 2000;
            display: flex; flex-direction: column; background: white;
        }
        .iframe-topbar {
            display: flex; align-items: center; justify-content: space-between;
            padding: 0.75rem 1rem; background: #1a1a1a; color: white; flex-shrink: 0;
        }
        .iframe-topbar-title { font-size: 0.95rem; font-weight: 600; }
        .iframe-close-btn {
            background: rgba(255,255,255,0.15); border: none; color: white;
            padding: 0.4rem 0.85rem; border-radius: 6px;
            cursor: pointer; font-family: inherit; font-size: 0.88rem;
            display: flex; align-items: center; gap: 0.4rem;
        }
        .iframe-close-btn:active { background: rgba(255,255,255,0.3); }
        .iframe-loading {
            flex: 1; display: flex; flex-direction: column;
            align-items: center; justify-content: center; gap: 1.25rem;
            background: #f5f7fa; text-align: center; padding: 2rem;
        }
        .iframe-loading i { font-size: 2.5rem; color: #2d6a2d; }
        .iframe-loading-title { font-size: 1.1rem; font-weight: 700; color: #1a1a1a; }
        .iframe-loading-sub { font-size: 0.9rem; color: #888; }
        iframe { flex: 1; border: none; width: 100%; }
    `],
    template: `
        <div class="pay-card">
            <div class="pay-header">{{ node.props?.title || 'Payment &amp; Invoice' }}</div>
            <div class="pay-body">

                <!-- ── Balance ─────────────────────────────────────────────── -->
                <div class="balance">
                    <div class="balance-row">
                        <span>Job total</span><b>{{ jobTotal | currency:currency }}</b>
                    </div>
                    <div class="balance-row">
                        <span>Already paid</span><b>{{ jobPaid | currency:currency }}</b>
                    </div>
                    <div class="balance-row due">
                        <span>Balance due</span><b>{{ jobDue | currency:currency }}</b>
                    </div>
                </div>

                <!-- ── 1 · Invoice ─────────────────────────────────────────── -->
                <div class="block" [class.active]="!invoice" [class.done]="!!invoice">
                    <div class="block-title"><span class="block-num">1</span> Invoice</div>

                    <ng-container *ngIf="!invoice">
                        <p class="muted" style="margin:0 0 0.55rem;">
                            Create the invoice for this move so the payment can be recorded against it.
                        </p>
                        <button type="button" class="btn btn-primary" (click)="createInvoice()" [disabled]="creatingInvoice">
                            <i class="pi" [ngClass]="creatingInvoice ? 'pi-spin pi-spinner' : 'pi-file'"></i>
                            {{ creatingInvoice ? 'Creating…' : 'Create invoice' }}
                        </button>
                        <p class="error-text" *ngIf="invoiceError">{{ invoiceError }}</p>
                    </ng-container>

                    <ng-container *ngIf="invoice">
                        <div class="done-line">
                            <i class="pi pi-check-circle"></i>
                            Invoice {{ invoice.invoiceNumber ? '#' + invoice.invoiceNumber : 'created' }}
                        </div>
                        <p class="muted" *ngIf="otherUninvoicedEvents > 0" style="margin:0.4rem 0 0;">
                            This job has {{ otherUninvoicedEvents }} other event(s) not on any invoice.
                            They are not included here.
                        </p>
                        <button type="button" class="btn btn-ghost" style="margin-top:0.55rem;"
                                *ngIf="invoiceArtifactId"
                                (click)="viewDocument()">
                            <i class="pi pi-eye"></i> View invoice
                        </button>
                        <p class="error-text" *ngIf="documentError">{{ documentError }}</p>
                    </ng-container>
                </div>

                <!-- ── 2 · Record payment ──────────────────────────────────── -->
                <div class="block"
                     [class.locked]="!invoice"
                     [class.active]="!!invoice && !recorded"
                     [class.done]="recorded">
                    <div class="block-title"><span class="block-num">2</span> Record payment</div>

                    <ng-container *ngIf="!recorded">
                        <div class="field">
                            <label class="field-label" for="nav-pay-amount">Amount received</label>
                            <div class="amount-wrap">
                                <input id="nav-pay-amount" class="amount-input" type="number"
                                       inputmode="decimal" min="0" [max]="maxAmount"
                                       [(ngModel)]="amount" [disabled]="recording">
                                <button type="button" class="max-btn" (click)="setMax()" [disabled]="recording">Max</button>
                            </div>
                            <p class="muted" style="margin:0.3rem 0 0;">
                                Maximum {{ maxAmount | currency:currency }}
                            </p>
                        </div>

                        <div class="field">
                            <span class="field-label">How they paid</span>
                            <div class="methods">
                                <button type="button" class="method-chip"
                                        *ngFor="let m of paymentMethods"
                                        [class.selected]="m.id === paymentTypeId"
                                        [disabled]="recording"
                                        (click)="paymentTypeId = m.id">{{ m.name }}</button>
                            </div>
                            <p class="muted" *ngIf="!paymentMethods.length" style="margin:0.3rem 0 0;">
                                No payment methods are configured for this zone.
                            </p>
                        </div>

                        <div class="field">
                            <label class="field-label" for="nav-pay-note">Note (optional)</label>
                            <input id="nav-pay-note" class="note-input" type="text"
                                   [(ngModel)]="note" [disabled]="recording"
                                   placeholder="Cheque number, reference…">
                        </div>

                        <button type="button" class="btn btn-primary" (click)="recordPayment()" [disabled]="!canRecord">
                            <i class="pi" [ngClass]="recording ? 'pi-spin pi-spinner' : 'pi-dollar'"></i>
                            {{ recording ? 'Recording…' : 'Record payment' }}
                        </button>
                        <p class="error-text" *ngIf="paymentError">{{ paymentError }}</p>
                    </ng-container>

                    <div class="done-line" *ngIf="recorded">
                        <i class="pi pi-check-circle"></i>
                        Recorded — {{ recordedAmount | currency:currency }} {{ recordedMethod }}
                    </div>
                </div>

                <!-- ── 3 · Send to customer ────────────────────────────────── -->
                <div class="block"
                     [class.locked]="!invoice"
                     [class.active]="!!invoice && !sent"
                     [class.done]="sent">
                    <div class="block-title"><span class="block-num">3</span> Send to customer</div>

                    <ng-container *ngIf="!sent">
                        <div class="warn">
                            <i class="pi pi-exclamation-triangle"></i>
                            <span>
                                Sending finalizes this invoice. No further charges can be added to the
                                invoiced events afterwards.
                            </span>
                        </div>

                        <ng-container *ngIf="!confirmingSend">
                            <button type="button" class="btn btn-send" (click)="confirmingSend = true">
                                <i class="pi pi-send"></i> Send invoice
                            </button>
                        </ng-container>

                        <ng-container *ngIf="confirmingSend">
                            <p class="muted" style="margin:0 0 0.5rem;">
                                Send the invoice to {{ customerEmail || 'the customer' }}? This cannot be undone.
                            </p>
                            <button type="button" class="btn btn-send" (click)="sendInvoice()" [disabled]="sending">
                                <i class="pi" [ngClass]="sending ? 'pi-spin pi-spinner' : 'pi-send'"></i>
                                {{ sending ? 'Sending…' : 'Yes, send it now' }}
                            </button>
                            <button type="button" class="btn btn-ghost" (click)="confirmingSend = false" [disabled]="sending">
                                Not yet
                            </button>
                        </ng-container>
                        <p class="error-text" *ngIf="sendError">{{ sendError }}</p>
                    </ng-container>

                    <div class="done-line" *ngIf="sent">
                        <i class="pi pi-check-circle"></i> Invoice sent
                    </div>

                    <!--
                        SLOT: customer signing goes HERE, after the send — never before it.
                        See the header comment and P13. Adding it above this point breaks sending.
                    -->
                </div>

                <p class="muted" style="margin:1rem 0 0;">
                    Nothing here is required — continue if the customer is being billed later.
                </p>
            </div>
        </div>

        <!-- Full-screen document viewer -->
        <div class="iframe-overlay" *ngIf="viewerOpen">
            <div class="iframe-topbar">
                <span class="iframe-topbar-title">Invoice</span>
                <button type="button" class="iframe-close-btn" (click)="closeViewer()">
                    <i class="pi pi-times"></i> Close
                </button>
            </div>

            <div class="iframe-loading" *ngIf="!documentUrl">
                <i class="pi pi-spin pi-spinner"></i>
                <div>
                    <div class="iframe-loading-title">Opening invoice…</div>
                    <div class="iframe-loading-sub">Getting the document from the server</div>
                </div>
            </div>

            <iframe *ngIf="documentUrl" [src]="documentUrl"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"></iframe>
        </div>
    `,
})
export class NavCollectPaymentComponent extends NavBaseComponent implements OnInit, OnDestroy {

    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly route = inject(ActivatedRoute);
    private readonly apollo = inject(Apollo);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly configGQL = inject(GetConfigValuesGQL);
    private readonly createTransactionGQL = inject(CreateTransactionGQL);
    private readonly aegisLinkGQL = inject(GetAegisDocumentLinkGQL);

    private subs = new Subscription();

    /** The job, kept live so the balance reflects a payment the moment it is recorded. */
    private job: any = null;
    private eventId = '';

    /**
     * The crew's current step, held rather than read on demand.
     *
     * `Store.selectSignal` needs an injection context, and everything that writes the step summary
     * here runs from a click handler or a network callback, where there isn't one.
     */
    private currentStepId = '';

    invoice: NavInvoice | null = null;
    otherUninvoicedEvents = 0;

    paymentMethods: PaymentMethod[] = [];
    paymentTypeId = '';
    amount = 0;
    note = '';

    creatingInvoice = false;
    invoiceError = '';

    recording = false;
    recorded = false;
    recordedAmount = 0;
    recordedMethod = '';
    paymentError = '';

    confirmingSend = false;
    sending = false;
    sent = false;
    sendError = '';

    viewerOpen = false;
    documentUrl: SafeResourceUrl | null = null;
    documentError = '';

    ngOnInit(): void {
        this.eventId = this.route.snapshot.paramMap.get('eventId') ?? '';

        this.subs.add(this.store.select(selectJob).pipe(filter(Boolean)).subscribe((job: any) => {
            const firstLoad = !this.job;
            this.job = job;
            if (firstLoad) { this.loadInvoices(); }
        }));

        // Not async-pipeable: this value is never rendered, it is read inside click handlers when
        // writing the step summary. Same reason the other navigator components disable this rule.
        // eslint-disable-next-line @ngrx/no-store-subscription
        this.subs.add(this.store.select(selectNavigatorCurrentStepId).subscribe((stepId) => {
            this.currentStepId = stepId ?? '';
        }));

        this.loadPaymentMethods();
    }

    ngOnDestroy(): void { this.subs.unsubscribe(); }

    // ── Derived figures ──────────────────────────────────────────────────────
    //
    // Job totals are cents on the wire; everything the crew sees and types is dollars.

    get currency(): string { return this.job?.currency || 'USD'; }
    get jobTotal(): number { return (this.job?.total ?? 0) / 100; }
    get jobPaid(): number { return (this.job?.paidTotal ?? 0) / 100; }
    get jobDue(): number { return Math.max(0, this.jobTotal - this.jobPaid); }

    /**
     * The same figure the desktop dialog's `setMaxValue()` computes — the lesser of what the invoice
     * still owes and what the job still owes, floored at zero. Before an invoice exists there is only
     * the job side to go on.
     */
    get maxAmount(): number {
        const jobBalance = this.jobDue;
        if (!this.invoice) { return jobBalance; }
        const invoiceBalance = ((this.invoice.total ?? 0) - (this.invoice.paidTotal ?? 0)) / 100;
        return Math.max(0, Math.min(invoiceBalance, jobBalance));
    }

    get customerEmail(): string {
        return this.job?.users?.find((u: any) => u.role === 'customer')?.user?.email ?? '';
    }

    private get customerId(): string {
        return this.job?.users?.find((u: any) => u.role === 'customer')?.user?.id ?? '';
    }

    private get invoiceArtifact() { return this.invoice?.artifacts?.[0] ?? null; }

    get invoiceArtifactId(): string { return this.invoiceArtifact?.id ?? ''; }

    /**
     * Whether the invoice document came from Aegis.
     *
     * `attributes` carries the engine tag — the same test the backend's `isAegisArtifact` makes. It
     * decides how the document can be shown at all, so it is read rather than assumed: a zone still
     * on PandaDoc has no Aegis link to mint, which is exactly why the first version of this button
     * failed with "could not be opened on this device" on every PandaDoc job.
     */
    private get invoiceIsAegis(): boolean {
        return (this.invoiceArtifact?.attributes ?? []).includes('aegis');
    }

    get canRecord(): boolean {
        return !this.recording
            && !!this.invoice
            && !!this.paymentTypeId
            && !!this.customerId
            && this.amount > 0
            && this.amount <= this.maxAmount + 0.001;
    }

    setMax(): void { this.amount = this.maxAmount; }

    // ── Loading ──────────────────────────────────────────────────────────────

    /**
     * Payment methods, minus anything backed by a provider.
     *
     * A `provider` means the payment type expects the *system* to take the money: `stripe` throws
     * `Stripe has not been implemented` outright, `square` diverts into minting a payment link, and
     * either way the backend forces `stage: 'pending'`. This component records money already
     * received, so offering those would hand the crew a button that errors mid-move.
     */
    private loadPaymentMethods(): void {
        this.subs.add(this.configGQL.watch({ keys: [ 'payment-type.*' ] }).valueChanges.subscribe((res) => {
            const configs = res?.data?.getConfigValues ?? [];
            const methods: PaymentMethod[] = [];

            for (const config of configs) {
                try {
                    const parsed = JSON.parse(config.value);
                    if (parsed?.name && parsed.enabled && !parsed.provider) {
                        methods.push({ id: config.id, name: parsed.name });
                    }
                } catch {
                    continue;
                }
            }

            this.paymentMethods = methods;
            if (!this.paymentTypeId && methods.length === 1) { this.paymentTypeId = methods[0].id; }
        }));
    }

    /**
     * Find the invoice covering this event, if there already is one.
     *
     * Runs on mount as well as after creating, so a crew who reloads — or whose dispatcher invoiced
     * from the office while they were driving — is shown the existing invoice rather than offered a
     * second one.
     */
    private loadInvoices(): void {
        if (!this.job?.id) { return; }

        this.subs.add(this.apollo.query<any>({
            query: INVOICES_FOR_JOB,
            variables: { jobId: this.job.id },
            fetchPolicy: 'network-only',
        }).subscribe({
            next: (res) => {
                const invoices: NavInvoice[] = (res?.data?.invoices?.invoices ?? [])
                    .filter((i: NavInvoice) => !i.deletedAt && !i.voidedAt);

                this.invoice = invoices.find(
                    (i) => (i.events ?? []).some((e) => e.id === this.eventId),
                ) ?? null;

                if (this.invoice?.sentAt) { this.sent = true; }

                const invoicedEventIds = new Set(
                    invoices.flatMap((i) => (i.events ?? []).map((e) => e.id)),
                );
                this.otherUninvoicedEvents = (this.job?.events ?? [])
                    .filter((e: any) => e.id !== this.eventId && !invoicedEventIds.has(e.id))
                    .length;

                if (this.amount === 0) { this.setMax(); }
            },
            error: (err) => {
                console.warn('[nav-collect-payment] could not load invoices', err);
            },
        }));
    }

    // ── 1 · Invoice ──────────────────────────────────────────────────────────

    createInvoice(): void {
        if (this.creatingInvoice || this.invoice || !this.job?.id || !this.eventId) { return; }

        this.creatingInvoice = true;
        this.invoiceError = '';

        // Reuse the job page's action rather than calling `createInvoice` directly: its reducer
        // patches the `jobs` slice, which is the slice `selectJob` reads, so the balance shown above
        // stays right without a refetch.
        // Bounded wait, because the outcome is not entirely ours to control: the `createInvoice`
        // effect only fires immediately when the job page has no unsaved changes, and otherwise waits
        // for a save that the navigator never triggers. Without the timeout that combination leaves a
        // spinner turning for the rest of the move.
        const eventId = this.eventId;

        this.subs.add(this.actions$.pipe(
            ofType(JobInvoiceActions.createInvoiceSuccess, JobInvoiceActions.createInvoiceError),
            // Correlate before `take(1)`. These are global action streams, so without this the first
            // create *from anywhere* resolves the wait and this step reports an outcome for an
            // operation it never observed.
            filter((a: any) => a.type === JobInvoiceActions.createInvoiceError.type
                || (a.eventIds ?? []).includes(eventId)),
            take(1),
            timeout(ACTION_TIMEOUT_MS),
            catchError(() => of(null)),
        ).subscribe((action: any) => {
            this.creatingInvoice = false;

            if (!action) {
                this.invoiceError = 'The invoice is taking too long. Check the job before trying again.';
                return;
            }

            if (action.type === JobInvoiceActions.createInvoiceError.type) {
                this.invoiceError = 'Could not create the invoice. Tap to try again.';
                return;
            }

            this.loadInvoices();
        }));

        this.store.dispatch(JobInvoiceActions.createInvoiceButtonClicked({
            eventIds: [ this.eventId ],
            jobId: this.job.id,
        }));
    }

    // ── 2 · Record payment ───────────────────────────────────────────────────

    recordPayment(): void {
        if (!this.canRecord) { return; }

        this.recording = true;
        this.paymentError = '';

        const method = this.paymentMethods.find((m) => m.id === this.paymentTypeId);
        const amount = this.amount;

        this.subs.add(this.createTransactionGQL.mutate({
            amount: Math.round(amount * 100),
            customerId: this.customerId,
            jobId: this.job.id,
            invoiceId: this.invoice!.id,
            type: TransactionType.Payment,
            paymentTypeId: this.paymentTypeId,
            stage: TransactionStage.Paid,
            // P13: deliberately suppressed. Generation and sending are the same call, so this also
            // means no receipt document exists — the decision is recorded, not incidental.
            disableSendReceipt: true,
            // A Quill delta, which is what the desktop dialog writes and what readers expect.
            ...(this.note ? { remarks: JSON.stringify({ ops: [ { insert: `${this.note}\n` } ] }) } : {}),
        }, {
            // The desktop dialog resolves the job's zone with an extra round trip; the job in the
            // store already carries it.
            context: { headers: { 'x-zone': this.job?.zone?.id } },
        }).subscribe({
            next: (res) => {
                this.recording = false;
                this.recorded = true;
                this.recordedAmount = amount;
                this.recordedMethod = method?.name ?? '';

                if (res?.data) {
                    this.store.dispatch(JobFinancialsActions.createTransactionSuccess({ data: res.data as any }));
                }

                this.persistSummary();
                this.loadInvoices();
            },
            error: (err) => {
                // Money: never fail silently, and never re-arm the button by accident. The form keeps
                // its values so the crew can retry without retyping.
                this.recording = false;
                this.paymentError = err?.message
                    ? `Could not record the payment: ${err.message}`
                    : 'Could not record the payment. Check the connection and try again.';
                console.error('[nav-collect-payment] createTransaction failed', err);
            },
        }));
    }

    // ── 3 · Send ─────────────────────────────────────────────────────────────

    sendInvoice(): void {
        if (this.sending || this.sent || !this.invoice) { return; }

        this.sending = true;
        this.sendError = '';

        const invoiceId = this.invoice.id;

        this.subs.add(this.actions$.pipe(
            ofType(JobInvoiceActions.sendInvoiceSuccess, JobInvoiceActions.sendInvoiceError),
            // Same correlation as the create above, and it matters more here: an unrelated send
            // would otherwise set `sent = true` and write `invoiceSent: true` into `navigatorData`
            // for an invoice this crew never sent.
            filter((a: any) => a.invoiceId === invoiceId),
            take(1),
            timeout(ACTION_TIMEOUT_MS),
            catchError(() => of(null)),
        ).subscribe((action: any) => {
            this.sending = false;
            this.confirmingSend = false;

            if (!action) {
                // Deliberately does NOT say "not sent". A timeout means we stopped listening, not
                // that the send failed — and this one finalizes, so inviting a blind retry is worse
                // than telling the crew to look.
                this.sendError = 'No confirmation came back. Check the job before sending again.';
                return;
            }

            if (action.type === JobInvoiceActions.sendInvoiceError.type) {
                this.sendError = 'Could not send the invoice. It has not been finalized — try again.';
                return;
            }

            this.sent = true;
            this.persistSummary();
        }));

        this.store.dispatch(JobInvoiceActions.sendInvoiceButtonClicked({ invoiceId: this.invoice.id }));
    }

    // ── Document viewer ──────────────────────────────────────────────────────

    /**
     * Show the generated invoice.
     *
     * Uses `getAegisDocumentLink`, which mints a role-scoped page and has no side effects.
     * Opened as `employee`: this is the crew checking their own paperwork. Showing it to the
     * customer would mean opening it as `customer`, and since there is no view-only Aegis role that
     * is also handing them a signing surface — a product decision (P13), not a default.
     *
     * The PDF artifact URL is deliberately not used: iOS browsers do not render embedded PDFs, and
     * this is a phone surface.
     */
    viewDocument(): void {
        const artifact = this.invoiceArtifact;
        if (!artifact) { return; }

        this.documentError = '';

        // Instrumentation, deliberately kept: the first version of this button failed differently on
        // phone and desktop and neither symptom named its cause. No identifiers beyond the artifact
        // id, which is not personal data.
        console.log('[collect-payment] view invoice', {
            artifactId: artifact.id,
            isAegis: this.invoiceIsAegis,
            attributes: artifact.attributes,
            hasSignedUrl: !!artifact.signedUrl,
            hasUrl: !!artifact.url,
        });

        // ── PandaDoc (or anything not Aegis): open the PDF itself ─────────────────────────
        //
        // There is no Aegis link to mint for these, which is what broke the first version — it
        // always asked for one and reported "could not be opened on this device" on every PandaDoc
        // job. The PDF is opened in a NEW TAB rather than the overlay on purpose: iOS browsers do
        // not render embedded PDFs, so an iframe here is a guaranteed blank frame on half the
        // crews' phones. A new tab uses the OS viewer and works everywhere.
        if (!this.invoiceIsAegis) {
            const pdfUrl = artifact.signedUrl || artifact.url || '';
            if (!pdfUrl) {
                this.documentError = 'The invoice document is not ready yet. Try again in a moment.';
                return;
            }
            window.open(pdfUrl, '_blank');
            return;
        }

        // ── Aegis: mint a role-scoped page and show it inline ─────────────────────────────
        //
        // `getAegisDocumentLink` has no side effects. Opened as `employee`: this is
        // the crew checking their own paperwork. Showing it to the customer means opening it as
        // `customer`, and since Aegis has no view-only role that also hands them a signing surface —
        // a product decision (P13), not a default.
        this.viewerOpen = true;
        this.documentUrl = null;

        this.subs.add(this.aegisLinkGQL.fetch(
            { artifactId: artifact.id, role: 'employee' },
            { fetchPolicy: 'no-cache' },
        ).subscribe({
            next: (res) => {
                const link = res?.data?.getAegisDocumentLink;
                if (!link) {
                    this.viewerOpen = false;
                    this.documentError = 'The invoice document is not ready yet.';
                    return;
                }
                this.documentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(link);
            },
            error: (err) => {
                this.viewerOpen = false;
                this.documentError = 'Could not open the invoice. Tap to try again.';
                console.warn('[collect-payment] getAegisDocumentLink failed', err?.message ?? err, err);
            },
        }));
    }

    closeViewer(): void {
        this.viewerOpen = false;
        this.documentUrl = null;
    }

    // ── Step record ──────────────────────────────────────────────────────────

    /**
     * Record what happened on this step in `navigatorData`, so the step's system comment can say it.
     *
     * The same route the photo and signature captures take. The step id comes from the slice rather
     * than being assumed, for the reason spelled out in `nav-capture-storage.ts`: a component's key
     * bears no relation to the step it sits on.
     */
    private persistSummary(): void {
        if (!this.eventId) { return; }

        this.store.dispatch(quickJsActions.navigatorDataPatched({
            eventId: this.eventId,
            patch: {
                collectPayment: {
                    stepId: this.currentStepId,
                    invoiceId: this.invoice?.id ?? null,
                    invoiceSent: this.sent,
                    paymentRecorded: this.recorded,
                    paymentAmount: this.recorded ? this.recordedAmount : null,
                    paymentMethod: this.recorded ? this.recordedMethod : null,
                    currency: this.currency,
                },
                // The report column `navigator_paymentMethod` (NAVIGATOR_NAMED_COLUMNS) only fills
                // from a top-level scalar — the nested copy above is flattened as a JSON string under
                // `navigator_collectPayment` (#85). Omitted rather than null until a payment is
                // recorded: the backend stores a null as the string "null".
                ...(this.recorded && this.recordedMethod ? { paymentMethod: this.recordedMethod } : {}),
            },
        }));
    }
}
